import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Switch,
  CircularProgress,
  Breadcrumbs,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { get, post } from "src/utils/networkUtils";

const MAX_PLAYER_PRICE = 10;

// Forwards are stored as "STR" - that is what the OPTA import writes and what
// fans see in the game.
const POSITIONS = ["GK", "DEF", "MID", "STR"];

const NewPlayerPage = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    matchName: "",
    position: "MID",
    price: "0",
    optaPersonId: "",
    contestantId: "",
    isVisible: true,
  });

  useEffect(() => {
    get("/admin/players/teams")
      .then((response) => {
        const data = response?.data ?? response;
        setTeams(data || []);
      })
      .catch((error) => {
        console.error("Failed to fetch teams:", error);
        setTeams([]);
      });
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.position) {
      newErrors.position = "Position is required";
    }

    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      newErrors.price = "Price must be 0 or greater";
    } else if (price > MAX_PLAYER_PRICE) {
      newErrors.price = `Price cannot exceed £${MAX_PLAYER_PRICE.toFixed(1)}m`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    navigate("/dashboard/players");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        position: formData.position,
        price: parseFloat(formData.price),
        isVisible: formData.isVisible,
      };

      const matchName = formData.matchName.trim();
      if (matchName) payload.matchName = matchName;

      const optaPersonId = formData.optaPersonId.trim();
      if (optaPersonId) payload.optaPersonId = optaPersonId;

      if (formData.contestantId) payload.contestantId = formData.contestantId;

      await post("/admin/players", payload);
      navigate("/dashboard/players");
    } catch (error) {
      console.error("Failed to create player:", error);
      setErrors({
        submit: error.response?.data?.message || "Failed to create player",
      });
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Breadcrumbs sx={{ mb: 2 }}>
        <Button
          onClick={handleBack}
          sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
        >
          Players
        </Button>
        <Typography color="text.primary">Add Player</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Add Player</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Match Name"
                value={formData.matchName}
                onChange={(e) => handleChange("matchName", e.target.value)}
                placeholder="N/A"
                helperText="Optional - most commonly known player name"
                fullWidth
              />
            </Grid>
          </Grid>

          <FormControl fullWidth error={!!errors.position}>
            <InputLabel id="position-label">Position</InputLabel>
            <Select
              labelId="position-label"
              value={formData.position}
              label="Position"
              onChange={(e) => handleChange("position", e.target.value)}
            >
              {POSITIONS.map((position) => (
                <MenuItem key={position} value={position}>
                  {position}
                </MenuItem>
              ))}
            </Select>
            {errors.position && (
              <FormHelperText>{errors.position}</FormHelperText>
            )}
          </FormControl>

          <TextField
            label="Price"
            type="number"
            value={formData.price}
            onChange={(e) => handleChange("price", e.target.value)}
            error={!!errors.price}
            helperText={
              errors.price ||
              `Enter price between £0.0m and £${MAX_PLAYER_PRICE.toFixed(1)}m`
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">m</InputAdornment>,
            }}
            inputProps={{
              step: 0.1,
              min: 0,
              max: MAX_PLAYER_PRICE,
            }}
            fullWidth
          />

          <TextField
            label="Opta Person ID"
            value={formData.optaPersonId}
            onChange={(e) => handleChange("optaPersonId", e.target.value)}
            helperText="Optional - must be unique"
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="team-label">Club / Team</InputLabel>
            <Select
              labelId="team-label"
              value={formData.contestantId}
              label="Club / Team"
              onChange={(e) => handleChange("contestantId", e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Optional</FormHelperText>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isVisible}
                onChange={(e) => handleChange("isVisible", e.target.checked)}
              />
            }
            label="Visible to Users"
          />

          {errors.submit && (
            <Typography color="error" variant="body2">
              {errors.submit}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleBack} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Creating..." : "Create Player"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default NewPlayerPage;
